import type {
	AccreditationAccessResult,
	AccreditationActions,
	AccreditationBulkFilter,
	AccreditationBulkResult,
	AccreditationBulkStatusBody,
	AccreditationBulkUrgentBody,
	AccreditationDeleteResult,
	AccreditationIdCheck,
	AccreditationWriteResult,
	CreateAccreditationBody,
	DeleteAccreditationBody,
	IdentificationType,
	PatchAccreditationBody,
	ResetCardAccessBody,
	SetAccreditationStatusBody,
	SetCardAccessBody,
	TransitionBody,
	AccreditationActivityDetail,
	AccreditationActivityPage,
	AccreditationListPage,
	AccreditationRecord,
	AccreditationSort,
	AccreditationSummary,
	AccreditationTravelFilter,
	AcCardTransitionAction,
	ErrorCode,
} from "@seagames/contracts";
import { isRefreshable } from "@seagames/contracts";
import { type InfiniteData, infiniteQueryOptions, keepPreviousData, queryOptions } from "@tanstack/react-query";
import { getAccessToken } from "../auth/token-store";
import { ApiError, api } from "./client";
import { queryClient } from "./query-client";

/**
 * The accreditation records API (M3): list (keyset), summary, record,
 * actions, activity, and the writes the records pages make. One file, so the
 * query keys and their invalidation live together.
 */

/**
 * The list's filters, in WIRE form (the `accreditationListQuery` keys):
 * `status` is the comma list the URL and the API both read. The summary and
 * the CSV export take the same object.
 */
export interface AccreditationFilter {
	games_id?: string;
	status?: string;
	responsible_organization_id?: string;
	main_function_id?: string;
	sub_agency_id?: string;
	sub_function_id?: string;
	urgent?: "true" | "false";
	multicard?: "true" | "false";
	/** M4: only cards with no photo that still stand. */
	no_photo?: "true" | "false";
	/** M9: with / without an arrival or departure leg (the A&D "approved without arrival" tile). */
	travel?: AccreditationTravelFilter;
	q?: string;
}

/** Compile-time check: this wire filter is exactly what the bulk endpoints accept as `filter`. */
const filterFitsBulk: AccreditationFilter extends AccreditationBulkFilter ? true : never = true;
void filterFitsBulk;

export interface AccreditationListParams extends AccreditationFilter {
	sort?: AccreditationSort;
	cursor?: string;
	limit: number;
}

/** Drop unset keys so equal filters make equal query keys. */
const clean = <T extends object>(o: T): T => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined && v !== "")) as T;

export const accreditationKeys = {
	all: ["accreditations"] as const,
	list: (q: AccreditationListParams) => ["accreditations", "list", clean(q)] as const,
	summary: (f: AccreditationFilter) => ["accreditations", "summary", clean(f)] as const,
	detail: (id: string) => ["accreditations", "detail", id] as const,
	actions: (id: string) => ["accreditations", "actions", id] as const,
	activity: (id: string) => ["accreditations", "activity", id] as const,
	activityDetail: (id: string, auditLogId: string) => ["accreditations", "activity", id, "detail", auditLogId] as const,
};

/** `GET /accreditations` — one keyset page; the total rides on the first page only. */
export const accreditationListOptions = (q: AccreditationListParams) =>
	queryOptions({
		queryKey: accreditationKeys.list(q),
		queryFn: ({ signal }) => api.get<AccreditationListPage>("/accreditations", { query: { ...clean(q) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 15_000,
	});

/** `GET /accreditations/summary` — status / function / organisation counts for the same filters, one aggregate. */
export const accreditationSummaryOptions = (f: AccreditationFilter) =>
	queryOptions({
		queryKey: accreditationKeys.summary(f),
		queryFn: ({ signal }) => api.get<AccreditationSummary>("/accreditations/summary", { query: { ...clean(f) }, signal }),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

export const accreditationRecordOptions = (id: string) =>
	queryOptions({
		queryKey: accreditationKeys.detail(id),
		queryFn: ({ signal }) => api.get<AccreditationRecord>(`/accreditations/${id}`, { signal }),
	});

/** What THIS actor may do to the card right now — the same SQL the writes use. */
export const accreditationActionsOptions = (id: string) =>
	queryOptions({
		queryKey: accreditationKeys.actions(id),
		queryFn: ({ signal }) => api.get<AccreditationActions>(`/accreditations/${id}/actions`, { signal }),
	});

/** The card's (and its person's) audit rows, newest first; "Show more" fetches the next keyset page. */
export const accreditationActivityOptions = (id: string) =>
	infiniteQueryOptions<AccreditationActivityPage, Error, InfiniteData<AccreditationActivityPage>, ReturnType<typeof accreditationKeys.activity>, string | undefined>({
		queryKey: accreditationKeys.activity(id),
		queryFn: ({ signal, pageParam }) =>
			api.get<AccreditationActivityPage>(`/accreditations/${id}/activity`, { query: { cursor: pageParam }, signal }),
		initialPageParam: undefined,
		getNextPageParam: (last) => last.next_cursor ?? undefined,
	});

/**
 * One write's before / after (SUPER_ADMIN only — the API answers 403 to anyone
 * else). Fetched when the headline is clicked, never for the whole feed. An
 * audit row never changes, so it is never stale.
 */
export const accreditationActivityDetailOptions = (id: string, auditLogId: string) =>
	queryOptions({
		queryKey: accreditationKeys.activityDetail(id, auditLogId),
		queryFn: ({ signal }) => api.get<AccreditationActivityDetail>(`/accreditations/${id}/activity/${auditLogId}`, { signal }),
		staleTime: Number.POSITIVE_INFINITY,
	});

/** After any write: every list, summary, record and feed may have moved. */
export const invalidateAccreditations = () => queryClient.invalidateQueries({ queryKey: accreditationKeys.all });

// ── Writes ──────────────────────────────────────────────────────────────────
// Bodies and results are S3's contracts (`accreditation-write.ts`).

export type { TransitionBody } from "@seagames/contracts";
export type BulkResult = AccreditationBulkResult;
export type BulkRefusalReason = AccreditationBulkResult["refused"][number]["reason"];

/** Either explicit ids (≤ BULK_ACCREDITATIONS_MAX_IDS) or the list's filter with the count the user saw ("Select all N"). */
export type BulkTarget = { ids: string[] } | { filter: AccreditationFilter; expected_count: number };

export const transitionAccreditation = (id: string, action: AcCardTransitionAction, body: TransitionBody) =>
	api.post<AccreditationWriteResult>(`/accreditations/${id}/${action}`, clean(body));

/**
 * SUPER_ADMIN override: any status to any other, off the graph, with a reason
 * and the card's `version` (→ STALE_VERSION; the same status → STATUS_UNCHANGED).
 * Offered exactly when `/actions` says `set_status`.
 */
export const setAccreditationStatus = (id: string, body: SetAccreditationStatusBody) =>
	api.post<AccreditationWriteResult>(`/accreditations/${id}/status`, body);

export const bulkAccreditationStatus = (body: AccreditationBulkStatusBody) => api.post<AccreditationBulkResult>("/accreditations/bulk/status", body);

export const bulkAccreditationUrgent = (urgent: boolean, target: BulkTarget) =>
	api.post<AccreditationBulkResult>("/accreditations/bulk/urgent", { urgent_printing: urgent, ...target } satisfies AccreditationBulkUrgentBody);

/**
 * Replace the card's access with exactly these codes (host only; at most one
 * TRANSPORT). A set that differs from the card's zone access rule marks it
 * `access_custom`: rule changes skip it until it is reset.
 */
export const setAccreditationAccess = (id: string, accessCodeIds: string[]) =>
	api.put<AccreditationAccessResult>(`/accreditations/${id}/access`, { access_code_ids: accessCodeIds } satisfies SetCardAccessBody);

/** Drop the card's hand-made access and follow its zone access rule again. Allowed exactly when `/actions` says `edit_access`. */
export const resetAccreditationAccess = (id: string, body: ResetCardAccessBody = {}) =>
	api.post<AccreditationAccessResult>(`/accreditations/${id}/access/reset`, body);

/** SUPER_ADMIN, DRAFT only, confirmed with the caller's own password. */
export const deleteAccreditation = (id: string, password: string) =>
	api.delete<AccreditationDeleteResult>(`/accreditations/${id}`, { confirm_password: password } satisfies DeleteAccreditationBody);

// ── CSV export ──────────────────────────────────────────────────────────────

const BASE_URL: string = import.meta.env.VITE_API_URL ?? "http://localhost:3001/api";

/**
 * `GET /accreditations/export.csv?…filters` → a file save. Not JSON, so it
 * cannot go through `api.get`; it uses the same Bearer token. An expired
 * token gets one retry after a cheap authenticated call refreshes it (the
 * client's single-flight refresh); any failure comes back as an `ApiError`
 * with the server's code, like every other request.
 *
 * TODO(lead): a `download` helper in `lib/api/client.ts` would let this reuse
 * the refresh path directly; that file is outside this slice.
 */
export async function downloadAccreditationsCsv(filter: AccreditationFilter, filename: string): Promise<void> {
	const url = new URL(`${BASE_URL}/accreditations/export.csv`, window.location.origin);
	for (const [k, v] of Object.entries(clean(filter))) url.searchParams.set(k, String(v));

	const attempt = async (): Promise<{ blob: Blob } | { error: ApiError }> => {
		const token = getAccessToken();
		let res: Response;
		try {
			res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "omit" });
		} catch (err) {
			if (err instanceof DOMException && err.name === "AbortError") throw err;
			return { error: new ApiError("SERVICE_UNAVAILABLE", 0, "Can't reach the server right now.") };
		}
		if (res.ok) return { blob: await res.blob() };
		// Like the client: the envelope's code decides, never the HTTP status.
		try {
			const body = (await res.json()) as { error?: { code?: ErrorCode; message?: string; details?: Record<string, unknown> } };
			if (body.error?.code) return { error: new ApiError(body.error.code, res.status, body.error.message ?? "", body.error.details) };
		} catch {
			/* not our envelope */
		}
		return { error: new ApiError("SERVICE_UNAVAILABLE", res.status, "Can't reach the server right now.") };
	};

	// A fresh page load holds only the refresh token: mint an access token first.
	if (!getAccessToken()) await api.get("/auth/me");
	let out = await attempt();
	if ("error" in out && isRefreshable(out.error.code)) {
		await api.get("/auth/me"); // refreshes (single-flight) — or throws the credential verdict
		out = await attempt();
	}
	if ("error" in out) throw out.error;
	saveBlob(out.blob, filename);
}

/** Hand a blob to the browser as a download. */
export function saveBlob(blob: Blob, filename: string): void {
	const href = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = href;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	setTimeout(() => URL.revokeObjectURL(href), 1000);
}

// ── Register (S5: `accreditation.add`, `accreditation.add.$id`) ──────────────

/** `POST /accreditations/check-id` — the duplicate guard (K6), before the Personal checkpoint creates anything. */
export const checkAccreditationId = (body: { identification_type: IdentificationType; identification_number: string }) =>
	api.post<AccreditationIdCheck>("/accreditations/check-id", body);

/** `POST /accreditations` — Zone + Personal (or `people_id`): creates the DRAFT and its registration number. */
export const createAccreditation = (body: CreateAccreditationBody) => api.post<AccreditationWriteResult>("/accreditations", body);

/** `PATCH /accreditations/:id` — any subset plus the `version` read (409 STALE_VERSION when someone saved first). */
export const patchAccreditation = (id: string, body: PatchAccreditationBody) => api.patch<AccreditationWriteResult>(`/accreditations/${id}`, body);
